import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  PageIcon,
  PageDescription,
  ContentGrid,
  ContentRow,
  Card,
  CardTitle,
  CardContent,
  ActionBar,
  Button,
  SearchInput,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  StatusBadge,
  TabContainer,
  TabList,
  Tab,
  TabContent,
  fadeIn
} from '../styles/PageStyles';
import { faChartColumn } from '@fortawesome/free-solid-svg-icons';
import PageHeaderWithInfo from '../components/common/PageHeaderWithInfo';

const Performance = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [timeRange, setTimeRange] = useState('week');

  // Mock data for agent performance
  const agentPerformance = [
    { name: 'Claims Analyst', accuracy: 94, speed: 89, satisfaction: 92, uptime: 99.8, usage: 78 },
    { name: 'Underwriter Assistant', accuracy: 96, speed: 85, satisfaction: 90, uptime: 99.9, usage: 65 },
    { name: 'Customer Service', accuracy: 91, speed: 94, satisfaction: 96, uptime: 99.7, usage: 82 },
    { name: 'Fraud Investigator', accuracy: 97, speed: 82, satisfaction: 88, uptime: 99.9, usage: 45 },
    { name: 'Document Processor', accuracy: 95, speed: 97, satisfaction: 89, uptime: 99.8, usage: 91 },
  ];

  // SLA metrics
  const slaMetrics = [
    { metric: 'Response Time', target: '< 2 seconds', current: '1.8 seconds', status: 'met' },
    { metric: 'Accuracy', target: '> 90%', current: '94.6%', status: 'met' },
    { metric: 'Uptime', target: '99.9%', current: '99.82%', status: 'warning' },
    { metric: 'First-Time Resolution', target: '> 85%', current: '87.3%', status: 'met' },
    { metric: 'Human Escalation Rate', target: '< 15%', current: '12.8%', status: 'met' },
  ];

  // Incident data
  const incidents = [
    { id: 'INC-001', type: 'Performance Degradation', service: 'Claims Processing', duration: '45 minutes', date: '2025-03-14', impact: 'medium', status: 'resolved' },
    { id: 'INC-002', type: 'API Latency', service: 'Document Processing', duration: '30 minutes', date: '2025-03-10', impact: 'low', status: 'resolved' },
    { id: 'INC-003', type: 'Model Accuracy Drop', service: 'Fraud Detection', duration: '2 hours', date: '2025-03-05', impact: 'high', status: 'resolved' },
    { id: 'INC-004', type: 'Service Outage', service: 'Customer Service', duration: '15 minutes', date: '2025-02-28', impact: 'medium', status: 'resolved' },
  ];

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'met': return 'active';
      case 'warning': return 'warning';
      case 'missed': return 'error';
      case 'resolved': return 'active';
      case 'investigating': return 'warning';
      default: return 'default';
    }
  };

  // Get impact badge color
  const getImpactColor = (impact) => {
    switch (impact) {
      case 'low': return 'default';
      case 'medium': return 'warning';
      case 'high': return 'error';
      default: return 'default';
    }
  };

  return (
    <PageContainer>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >
        <PageHeaderWithInfo
          icon={faChartColumn}
          title="Performance"
          description="Monitor and analyze the performance metrics of all AI agents and services in the system.
          Track SLA compliance, identify bottlenecks, and optimize resource allocation for maximum efficiency."
        >
          <div style={{ display: 'flex', gap: '1rem' }}>
            <Button onClick={() => setTimeRange('day')} primary={timeRange === 'day'}>Day</Button>
            <Button onClick={() => setTimeRange('week')} primary={timeRange === 'week'}>Week</Button>
            <Button onClick={() => setTimeRange('month')} primary={timeRange === 'month'}>Month</Button>
            <Button onClick={() => setTimeRange('quarter')} primary={timeRange === 'quarter'}>Quarter</Button>
          </div>
        </PageHeaderWithInfo>


        <TabContainer>
          <TabList>
            <Tab active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>Overview</Tab>
            <Tab active={activeTab === 'agents'} onClick={() => setActiveTab('agents')}>Agent Performance</Tab>
            <Tab active={activeTab === 'sla'} onClick={() => setActiveTab('sla')}>SLA Metrics</Tab>
            <Tab active={activeTab === 'incidents'} onClick={() => setActiveTab('incidents')}>Incidents</Tab>
          </TabList>

          <TabContent active={activeTab === 'overview'}>
            <ContentGrid style={{gridTemplateColumns: 'repeat(2, 1fr)'}}>
              <Card>
                <CardTitle>System Health</CardTitle>
                <CardContent>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: '#34C759',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 1rem auto',
                        fontSize: '1rem',
                        color: 'white',
                        fontWeight: 'bold'
                      }}>
                        94.6%
                      </div>
                      <div style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>Overall Health</div>
                    </div>

                    <div style={{ textAlign: 'center' }}>
                      <div style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: '#34C759',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 1rem auto',
                        fontSize: '1rem',
                        color: 'white',
                        fontWeight: 'bold'
                      }}>
                        99.8%
                      </div>
                      <div style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>Uptime</div>
                    </div>

                    <div style={{ textAlign: 'center' }}>
                      <div style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: '#FFCC00',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 1rem auto',
                        fontSize: '1rem',
                        color: 'white',
                        fontWeight: 'bold'
                      }}>
                        87.3%
                      </div>
                      <div style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>Resolution</div>
                    </div>

                    <div style={{ textAlign: 'center' }}>
                      <div style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: '#34C759',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 1rem auto',
                        fontSize: '1rem',
                        color: 'white',
                        fontWeight: 'bold'
                      }}>
                        1.8s
                      </div>
                      <div style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>Response Time</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardTitle>System Load</CardTitle>
                <CardContent>
                  <div style={{ height: '200px', display: 'flex', alignItems: 'flex-end', gap: '12px', marginTop: '20px' }}>
                    {[65, 72, 68, 75, 82, 79, 85].map((value, index) => (
                      <div key={index} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ height: `${value * 1.5}px`, width: '100%', background: 'var(--primary)', borderRadius: '4px 4px 0 0' }}></div>
                        <div style={{ fontSize: '0.7rem', marginTop: '5px' }}>{timeRange === 'day' ? `${index + 9}:00` : `D${index + 1}`}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    {timeRange === 'day' ? 'Hourly Load' : timeRange === 'week' ? 'Daily Load' : timeRange === 'month' ? 'Weekly Load' : 'Monthly Load'}
                  </div>
                </CardContent>
              </Card>
            </ContentGrid>

            <ContentRow>
              <Card style={{ flex: 1, minWidth: '300px' }}>
                <CardTitle>Top Performing Agents</CardTitle>
                <Table>
                  <thead>
                    <tr>
                      <TableHeader>Agent</TableHeader>
                      <TableHeader>Accuracy</TableHeader>
                      <TableHeader>Speed</TableHeader>
                      <TableHeader>Satisfaction</TableHeader>
                      <TableHeader>Usage</TableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {agentPerformance
                      .sort((a, b) => (a.accuracy + a.speed + a.satisfaction) / 3 < (b.accuracy + b.speed + b.satisfaction) / 3 ? 1 : -1)
                      .slice(0, 3)
                      .map((agent, index) => (
                        <TableRow key={index}>
                          <TableCell>{agent.name}</TableCell>
                          <TableCell>{agent.accuracy}%</TableCell>
                          <TableCell>{agent.speed}%</TableCell>
                          <TableCell>{agent.satisfaction}%</TableCell>
                          <TableCell>{agent.usage}%</TableCell>
                        </TableRow>
                      ))
                    }
                  </tbody>
                </Table>
              </Card>
            </ContentRow>
          </TabContent>

          <TabContent active={activeTab === 'agents'}>
            <Card>
              <CardTitle>Agent Performance Metrics</CardTitle>
              <Table>
                <thead>
                  <tr>
                    <TableHeader>Agent</TableHeader>
                    <TableHeader>Accuracy</TableHeader>
                    <TableHeader>Speed</TableHeader>
                    <TableHeader>User Satisfaction</TableHeader>
                    <TableHeader>Uptime</TableHeader>
                    <TableHeader>Usage</TableHeader>
                    <TableHeader>Overall</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {agentPerformance.map((agent, index) => {
                    const overall = Math.round((agent.accuracy + agent.speed + agent.satisfaction) / 3);
                    return (
                      <TableRow key={index}>
                        <TableCell>{agent.name}</TableCell>
                        <TableCell>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span>{agent.accuracy}%</span>
                            <div style={{ flex: 1, height: '6px', background: 'rgba(0,102,255,0.1)', borderRadius: '3px' }}>
                              <div style={{ width: `${agent.accuracy}%`, height: '6px', background: 'var(--primary)', borderRadius: '3px' }}></div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span>{agent.speed}%</span>
                            <div style={{ flex: 1, height: '6px', background: 'rgba(0,102,255,0.1)', borderRadius: '3px' }}>
                              <div style={{ width: `${agent.speed}%`, height: '6px', background: 'var(--primary)', borderRadius: '3px' }}></div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span>{agent.satisfaction}%</span>
                            <div style={{ flex: 1, height: '6px', background: 'rgba(0,102,255,0.1)', borderRadius: '3px' }}>
                              <div style={{ width: `${agent.satisfaction}%`, height: '6px', background: 'var(--primary)', borderRadius: '3px' }}></div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{agent.uptime}%</TableCell>
                        <TableCell>{agent.usage}%</TableCell>
                        <TableCell>
                          <StatusBadge status={overall >= 95 ? 'active' : overall >= 90 ? 'warning' : 'error'}>
                            {overall}%
                          </StatusBadge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </tbody>
              </Table>
            </Card>
          </TabContent>

          <TabContent active={activeTab === 'sla'}>
            <Card>
              <CardTitle>Service Level Agreement Metrics</CardTitle>
              <Table>
                <thead>
                  <tr>
                    <TableHeader>Metric</TableHeader>
                    <TableHeader>Target</TableHeader>
                    <TableHeader>Current</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Trend</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {slaMetrics.map((metric, index) => (
                    <TableRow key={index}>
                      <TableCell>{metric.metric}</TableCell>
                      <TableCell>{metric.target}</TableCell>
                      <TableCell>{metric.current}</TableCell>
                      <TableCell>
                        <StatusBadge status={getStatusColor(metric.status)}>
                          {metric.status.charAt(0).toUpperCase() + metric.status.slice(1)}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        {index % 2 === 0 ? '↗️' : index % 3 === 0 ? '↘️' : '→'}
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </Card>
          </TabContent>

          <TabContent active={activeTab === 'incidents'}>
            <ActionBar>
              <Button primary>Report Incident</Button>
              <Button>Filter</Button>
              <Button>Export Report</Button>
            </ActionBar>

            <Card>
              <CardTitle>Recent Incidents</CardTitle>
              <Table>
                <thead>
                  <tr>
                    <TableHeader>Incident ID</TableHeader>
                    <TableHeader>Type</TableHeader>
                    <TableHeader>Service</TableHeader>
                    <TableHeader>Duration</TableHeader>
                    <TableHeader>Date</TableHeader>
                    <TableHeader>Impact</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Actions</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {incidents.map((incident, index) => (
                    <TableRow key={index}>
                      <TableCell>{incident.id}</TableCell>
                      <TableCell>{incident.type}</TableCell>
                      <TableCell>{incident.service}</TableCell>
                      <TableCell>{incident.duration}</TableCell>
                      <TableCell>{incident.date}</TableCell>
                      <TableCell>
                        <StatusBadge status={getImpactColor(incident.impact)}>
                          {incident.impact}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={getStatusColor(incident.status)}>
                          {incident.status}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        <Button style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>Details</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </Card>
          </TabContent>
        </TabContainer>
      </motion.div>
    </PageContainer>
  );
};

export default Performance;
